import { DateTime } from "luxon";

import { baseDefinedAndNotNullNarrowing } from "@initia-digital/ts-utils";

import {
  IPlanMin,
  IPlanStatus,
  IPlanRegisterHazard,
  IHazardClass,
  IEquipmentClass,
  IEquipmentClassMin,
  IEquipmentClassUse,
  IEquipmentClassUseMin,
  ICriticalOrSiteSpecificHazard,
  IPrestart,
  IWellbeingSurvey,
  IFloatStaffCount,
  IHazard
} from "@initia-digital/types/work-and-safety";

// This file has been migrated from Work and Safety version 1
// The intention would be (if priorities allow) to migrate these utilities to the data-sdk or similar
// for broader consumption

// Business rule here is plan has to be in progress sometime in the next month
export const isRecentPlan = (jobPlan: IPlanMin) => {
  const startDate = DateTime.fromISO(jobPlan.startDate);
  const hasEndDate = baseDefinedAndNotNullNarrowing<string>(jobPlan.endDate);
  const endDate: DateTime = hasEndDate ? DateTime.fromISO(jobPlan.endDate) : DateTime.local().plus({ 'days': 1 });

  return (
    // Start date somewhere in the next month (or was in the last 3 days)
    (startDate.startOf('day') >= DateTime.local().minus({ day: 3 }).startOf('day') && !(startDate.startOf('day') > DateTime.local().startOf('day').plus({ month: 1 })))
    ||
    // OR end date sometime in the next month
    (hasEndDate && (endDate.startOf('day') >= DateTime.local().minus({ day: 3 }).startOf('day') && !(endDate.startOf('day') > DateTime.local().startOf('day').plus({ month: 1 }))))
  );
}

export const filterPlanForStatus = (jobPlan: IPlanMin, statuses: IPlanStatus[]) => {
  // ---------------------------------------------------
  return statuses.includes(jobPlan.status);
}

export const sortPlanByLatestEndDate = (jobPlanA: IPlanMin, jobPlanB: IPlanMin) => {
  const startDateA = DateTime.fromISO(jobPlanA.startDate);
  const aHasEndDate = baseDefinedAndNotNullNarrowing(jobPlanA.endDate);
  const endDateA: DateTime = aHasEndDate ? DateTime.fromISO(jobPlanA.endDate) : DateTime.local().plus({ 'days': 1 });
  const startDateB = DateTime.fromISO(jobPlanB.startDate);
  const bHasEndDate = baseDefinedAndNotNullNarrowing(jobPlanB.endDate);
  const endDateB: DateTime = bHasEndDate ? DateTime.fromISO(jobPlanB.endDate) : DateTime.local().plus({ 'days': 1 });

  if (endDateA.equals(endDateB)) {
    return startDateB.toMillis() - startDateA.toMillis();
  }
  else {
    return endDateB.toMillis() - endDateA.toMillis();
  }
}

export const sortPlanByEarliestStartDate = (jobPlanA: IPlanMin, jobPlanB: IPlanMin) => {
  const startDateA = DateTime.fromISO(jobPlanA.startDate);
  const aHasEndDate = baseDefinedAndNotNullNarrowing(jobPlanA.endDate);
  const endDateA: DateTime = aHasEndDate ? DateTime.fromISO(jobPlanA.endDate) : DateTime.local().plus({ 'days': 1 });
  const startDateB = DateTime.fromISO(jobPlanB.startDate);
  const bHasEndDate = baseDefinedAndNotNullNarrowing(jobPlanB.endDate);
  const endDateB: DateTime = bHasEndDate ? DateTime.fromISO(jobPlanB.endDate) : DateTime.local().plus({ 'days': 1 });

  if (startDateA.equals(startDateB)) {
    return endDateA.toMillis() - endDateB.toMillis();
  }
  else {
    return startDateA.toMillis() - startDateB.toMillis();
  }
}

export const sortPlanRegisterHazardClassPresentationOrder = (hazardA: IPlanRegisterHazard, hazardB: IPlanRegisterHazard, referenceHazardClassPresentationOrder: string[]) => {
  // -------------------------------------------
  // Presentation Order of A higher than B - therefore sort A below B
  if ((referenceHazardClassPresentationOrder.indexOf(hazardA.hazardClass.label) - referenceHazardClassPresentationOrder.indexOf(hazardB.hazardClass.label)) > 0) {
    return 1
  }
  // Presentation Order of A lower than B - therefore sort A above B
  else if ((referenceHazardClassPresentationOrder.indexOf(hazardA.hazardClass.label) - referenceHazardClassPresentationOrder.indexOf(hazardB.hazardClass.label)) < 0) {
    return -1;
  }
  // Presentation Order of A equal to B - therefore no sort preference
  else {
    return 0;
  }
}

export const sortHazardClassesByPresentationOrder = (hazardClassA: IHazardClass, hazardClassB: IHazardClass) => {
  const { presentationOrder: aPresentationOrder } = hazardClassA;
  const { presentationOrder: bPresentationOrder } = hazardClassB;
  const aPresentationOrderDefined = baseDefinedAndNotNullNarrowing(aPresentationOrder);
  const bPresentationOrderDefined = baseDefinedAndNotNullNarrowing(bPresentationOrder);

  if (!aPresentationOrderDefined
        && !bPresentationOrderDefined) {
      // No presentation orders available
      return 0;
  }
  else if (aPresentationOrderDefined
        && !bPresentationOrderDefined) {
      // Sort A higher than B
      return -1;
  }
  else if (!aPresentationOrderDefined
        && bPresentationOrderDefined) {
      // Sort B higher than A
      return 1;
  }
  else if (aPresentationOrderDefined 
        && bPresentationOrderDefined) {
    return Number(aPresentationOrder) - Number(bPresentationOrder);
  }
  else {
    // No sorting logic available
    return 0;
  }
}

export const convertHazardForPlan = (hazard: IHazard): IPlanRegisterHazard => {
  // --------------------------------------------
  const { id, hazardClassType, hazardClass, hazardName, hazardControls } = hazard;
  const { id: hazardClassId, value, label } = hazardClass;

  return {
    id,
    hazardClassType,
    hazardName,
    hazardControls,
    hazardClass: { id: hazardClassId, value, label }
  }
}


export const isEmptyCriticalHazard = (hazard: ICriticalOrSiteSpecificHazard) => {
  // --------------------------------------------------------

  return (
    !baseDefinedAndNotNullNarrowing(hazard.hazardName)
    && !baseDefinedAndNotNullNarrowing(hazard.description)
  )
}

export const convertEquipmentClassToMin = (equipment: IEquipmentClass): IEquipmentClassMin => {
  // --------------------------------------------
  const { id, name, type, notes } = equipment;

  return {
    id, name, type, notes
  }
}

export const convertEquipmentClassUseToMin = (equipmentClassUse: IEquipmentClassUse): IEquipmentClassUseMin => {
  // --------------------------------------------
  const { id, equipmentClassId, name, jobId, type, notes, createdBy, creationTimestamp, updatedBy, lastUpdateTimestamp } = equipmentClassUse;

  return {
    id, equipmentClassId, name, jobId, type, notes, createdBy, creationTimestamp, updatedBy, lastUpdateTimestamp
  }
}

export const sortPrestartByDate = (prestartA: IPrestart, prestartB: IPrestart) => {
  const dateA = DateTime.fromISO(prestartA.prestartTimestamp!);
  const dateB = DateTime.fromISO(prestartB.prestartTimestamp!);

  return dateB.toMillis() - dateA.toMillis();
}

// Business rule here is prestart has to be from sometime in the next month
export const isRecentPrestart = (prestart: IPrestart) => {
  const prestartTimestamp = DateTime.fromISO(prestart.prestartTimestamp);

  return (
    // Prestart timestamp somewhere in the last 2 weeks
    prestartTimestamp.startOf('day') >= DateTime.local().minus({ week: 2 }).startOf('day')
  );
}

export const sortWellbeingSurveyByDate = (surveyA: IWellbeingSurvey, surveyB: IWellbeingSurvey) => {
  const dateA = DateTime.fromISO(surveyA.creationTimestamp!);
  const dateB = DateTime.fromISO(surveyB.creationTimestamp!);

  return dateB.toMillis() - dateA.toMillis();
}

export const sortFloatStaffCountByDate = (countA: IFloatStaffCount, countB: IFloatStaffCount) => {
  const dateA = DateTime.fromISO(countA.timestamp!);
  const dateB = DateTime.fromISO(countB.timestamp!);

  return dateB.toMillis() - dateA.toMillis();
}

export const filterWellbeingSurveyWithinTimePeriod = (survey: IWellbeingSurvey) => {
  const surveyDate = DateTime.fromISO(survey.creationTimestamp!);

  return surveyDate > DateTime.local().minus({ 'years': 1 });
}